import {useCallback, useEffect, useRef, useState, useMemo} from 'react';
import {UseLazyScrollProps} from './types';
import {DEFAULT_LOADING_TIME, DEFAULT_ITEMS_PER_PAGE, INITIAL_PAGE} from './constants';

export function useLazyScroll<T>({
	loadingTime = DEFAULT_LOADING_TIME,
	itemsPerPage = DEFAULT_ITEMS_PER_PAGE,
	itemsList,
}: UseLazyScrollProps<T>) {
	const [loadingNewPageState, setLoadingNewPageState] = useState(false);
	const scrollableContentRef = useRef<HTMLDivElement>(null);
	const [currentPageState, setCurrentPageState] = useState(INITIAL_PAGE);

	const maxPage = useMemo(
		() => Math.ceil(itemsList.length / itemsPerPage),
		[itemsList, itemsPerPage],
	);

	const items = useMemo(() => {
		const indexOfLastItem = currentPageState * itemsPerPage;
		return itemsList ? itemsList.slice(0, indexOfLastItem) : [];
	}, [currentPageState, itemsPerPage, itemsList]);

	const currentPageIsTheLast = currentPageState >= maxPage;

	const handleVisible = useCallback(() => {
		if (!loadingNewPageState && !currentPageIsTheLast) {
			setLoadingNewPageState(true);
		}
	}, [loadingNewPageState, currentPageIsTheLast]);

	useEffect(() => {
		let timer: NodeJS.Timeout;
		if (loadingNewPageState) {
			timer = setTimeout(() => {
				setCurrentPageState(prevPage => prevPage + 1);
				setLoadingNewPageState(false);
			}, loadingTime);
		}
		return () => clearTimeout(timer);
	}, [loadingNewPageState]);

	return {
		scrollableContentRef,
		items,
		loadingNewPageState,
		currentPageState,
		currentPageIsTheLast,
		handleVisible,
	};
}
